﻿<template>
  <section class="small-12 medium-12 large-6 columns">
    <!-- /************* WHAT HAPPENED *************/ -->
    <h1 v-html="vueModel.ContentViewModel.Step2Car.StepTitle" />

    <!-- /************* RECOURSE *************/ -->
    <Article v-if="showRecourseImages" :title="vueModel.ContentViewModel.Step2Car.TitleRecourse">
      <div class="control">
        <Field
          name="recourse-images"
          v-slot="{ field, meta }"
          rules="required"
          :label="vueModel.ContentViewModel.Step2Car.ErrorMessageRecourseImages"
          v-model="vueModel.CreateClaimEntity.ProductSpecific.RaCode"
        >
          <BaseRadioImages
            v-bind="field"
            :options="recourseImageOptions"
            :model-value="vueModel.CreateClaimEntity.ProductSpecific.RaCode"
            :meta="meta"
            class="push-up"
            name="recourse-images"
          />
          <ValidationError name="recourse-images" />
        </Field>
      </div>
    </Article>

    <!-- /************* RESPONSIBILITY DISTRIBUTION *************/ -->
    <Article
      v-if="showResponsibilityDistribution"
      :title="vueModel.ContentViewModel.Step2Car.TitleResponsibilityDistribution"
    >
      <div class="control">
        <Field
          name="responsibility-distribution"
          v-slot="{ field }"
          rules="required"
          :label="vueModel.ContentViewModel.Step2Car.ErrorMessageResponsibilityDistribution"
          v-model="vueModel.CreateClaimEntity.ProductSpecific.ResponsibilityDistribution"
        >
          <RadioGroupInline
            v-bind="field"
            :options="stepModel.ProductSpecific.ResponsibilityDistributionOptions"
            :model-value="vueModel.CreateClaimEntity.ProductSpecific.ResponsibilityDistribution"
            :data-vv-as="vueModel.ContentViewModel.Step2Car.ErrorMessageResponsibilityDistribution"
            value-prop="Value"
          />
          <ValidationError :name="field.name" />
        </Field>
      </div>
    </Article>

    <!-- /************* DESCRIBE WHAT HAPPENED *************/ -->
    <Article :title="vueModel.ContentViewModel.Step2Car.TitleClaimEventDescription">
      <div class="control">
        <Field
          as="textarea"
          v-model="vueModel.CreateClaimEntity.ClaimEventDescription"
          :rules="'required'"
          cols="30"
          rows="3"
          name="describe-incident"
          :label="vueModel.ContentViewModel.Step2Car.ErrorMessageClaimEventDescription"
          :data-vv-as="vueModel.ContentViewModel.Step2Car.ErrorMessageClaimEventDescription"
        />
        <ValidationError name="describe-incident" />
      </div>

      <h3
        data-test-upload-photo-title
        v-html="vueModel.ContentViewModel.GeneralContent.TitleAttachPhotos"
      />

      <p
        data-test-upload-photo-text
        v-html="vueModel.ContentViewModel.GeneralContent.TextAttachPhotos"
      />

      <upload
        v-model="vueModel.files"
        :content="vueModel.ContentViewModel.Step4Shared"
        :title="vueModel.ContentViewModel.GeneralContent.SubtitleAttachPhotos"
      />
    </Article>

    <!-- /************* CHOSE CLAIM TYPE - QUESTIONAIRE *************/ -->
    <Article
      v-if="hasQuestionnaireQuestions"
      :title="vueModel.ContentViewModel.Step2Car.TitleClaimTypeCuestionaire"
    >
      <questionnaire :options="questions" :max-date="claimEventDate" />
    </Article>

    <!-- /************* WHO WAS THE DRIVER *************/ -->
    <Article :title="vueModel.ContentViewModel.Step2Car.TitleDriver">
      <div class="control">
        <Field
          name="driver"
          v-slot="{ field }"
          rules="required"
          :label="vueModel.ContentViewModel.Step2Car.ErrorMessageResponsibilityDistribution"
          v-model="vueModel.CreateClaimEntity.ProductSpecific.Driver"
        >
          <RadioGroupInline
            v-bind="field"
            :options="stepModel.ProductSpecific.DriversOptions"
            :model-value="vueModel.CreateClaimEntity.ProductSpecific.Driver"
            label-prop="Name"
            data-test="create-claim-flow-car-driver"
          />
          <ValidationError :name="field.name" />
        </Field>
      </div>

      <!-- /************* INPUT OTHER DRIVER FIELDS *************/ -->
      <div v-if="otherDriverRequired" id="other-driver">
        <!-- /*** OTHER DRIVER: FIRSTNAME ***/ -->
        <div class="control">
          <label
            for="other-driver-first-name"
            class="text-question"
            v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverFirstname"
          />
          <Field
            id="other-driver-first-name"
            v-model="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverFirstName"
            :rules="{ required: otherDriverRequired }"
            type="text"
            name="other-driver-first-name"
            :label="vueModel.ContentViewModel.Step2Car.ErrorMessageOtherDriverFirstname"
          />
          <ValidationError name="other-driver-first-name" />
        </div>
        <!-- /*** OTHER DRIVER: LASTNAME ***/ -->
        <div class="control">
          <label
            for="other-driver-last-name"
            class="text-question"
            v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverLastname"
          />
          <Field
            id="other-driver-last-name"
            v-model="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverLastName"
            :rules="{ required: otherDriverRequired }"
            type="text"
            name="other-driver-last-name"
            :label="vueModel.ContentViewModel.Step2Car.ErrorMessageOtherDriverLastname"
          />
          <ValidationError name="other-driver-last-name" />
        </div>
        <!-- /*** OTHER DRIVER: ADDRESS SEARCH ***/ -->
        <div class="control">
          <label
            for="other-driver-address"
            class="text-question"
            v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverAddressSearch"
          />
          <Field
            v-slot="{ field, errors }"
            :model-value="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverSelectedAddress"
            name="other-driver-address"
            :label="vueModel.ContentViewModel.Step2Car.ErrorMessageOtherDriverAddressSearch"
          >
            <address-search
              v-bind="field"
              :model-value="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverSelectedAddress"
              @update:model-value="(newValue) => (vueModel.CreateClaimEntity.ProductSpecific.OtherDriverSelectedAddress = newValue)"
              :name="field.name"
              :errors="errors"
            />
            <ValidationError :name="field.name" />
          </Field>

          <ValidationError name="other-driver-address" />
        </div>
        <div class="control row">
          <!-- /*** OTHER DRIVER: PHONE ***/ -->
          <div class="small-12 medium-6 columns">
            <label
              for="other-driver-phone"
              class="text-question"
              v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverPhone"
            />
            <Field
              id="other-driver-phone"
              v-model="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverPhone"
              :rules="{ required: otherDriverRequired, numeric: true, min: 8 }"
              type="text"
              name="other-driver-phone"
              maxlength="8"
              :label="vueModel.ContentViewModel.Step2Car.ErrorMessageOtherDriverPhone"
              :[`aria-invalid`]="errors['other-driver-phone'] ? true : false"
            />
            <ValidationError name="other-driver-phone" />
          </div>
          <!-- /*** OTHER DRIVER: EMAIL ***/ -->
          <div class="small-12 medium-6 columns">
            <label
              for="other-driver-email"
              class="text-question"
              v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverEmail"
            />
            <Field
              id="other-driver-email"
              v-model="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverEmail"
              :rules="{ email: true }"
              type="text"
              name="other-driver-email"
              :label="vueModel.ContentViewModel.Step2Car.ErrorMessageOtherDriverEmail"
              :[`aria-invalid`]="errors['other-driver-email'] ? true : false"
            />
            <ValidationError name="other-driver-email" />
          </div>
        </div>
        <!-- /*** OTHER DRIVER: AGE ***/ -->
        <div v-if="otherDriverBirthdayRequired" class="control">
          <label
            for="other-driver-select-age"
            class="text-question"
            v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverAge"
          />
          <Field
            name="other-driver-age"
            v-slot="{ field, meta }"
            :rules="{ required: vueModel.CreateClaimEntity.ProductSpecific.SelectedDriverAge }"
            v-model="vueModel.CreateClaimEntity.ProductSpecific.SelectedDriverAge"
          >
            <RadioGroupStacked
              v-bind="field"
              :options="stepModel.ProductSpecific.OtherDriverAgeOptions"
              :model-value="vueModel.CreateClaimEntity.ProductSpecific.SelectedDriverAge"
            />
            <ValidationError
               :name="field.name"
            :errors="errors"
            :overrides="{ required: 'Angiv venligst' }"
            v-if="meta.validated && !meta.valid"
            />
          </Field>
            <template v-if="vueModel.CreateClaimEntity.ProductSpecific.SelectedDriverAge && otherDriverBirthdayRequired">
              <label
                for="other-driver-select-knows-birthday"
                class="text-question"
                v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverIsBirthdayKnown"
              />
              <Field
              name="other-driver-birthday-known"
              v-slot="{ field, meta }"
              :rules="{ required: stepModel.ProductSpecific.OtherDriverIsBirthdayKnown === null }"
              v-model="stepModel.ProductSpecific.OtherDriverIsBirthdayKnown"
              >
                <RadioGroupStacked
                  v-bind="field"
                  :options="stepModel.ProductSpecific.OtherDriverIsBirthdayKnownOptions"
                  :model-value="stepModel.ProductSpecific.OtherDriverIsBirthdayKnown"
                />
                <ValidationError
                  :name="field.name"
                  :overrides="{ required: 'Angiv venligst' }"
                  :errors="errors"
                  v-if="meta.validated && !meta.valid"
                />
              </Field>
            </template>

          <template v-if="stepModel.ProductSpecific.OtherDriverIsBirthdayKnown">
            <label
              for="other-driver-select-knows-birthday"
              class="text-question"
              v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverBirthday"
            />
            <fieldset>
              <div class="row columns">
                <Field
                  name="other-driver-birthday"
                  v-slot="{ field }"
                  rules="required"
                  :label="vueModel.ContentViewModel.Step2Car.ErrorMessageOtherDriverBirthday"
                >
                  <date-picker
                    v-bind="field"
                    id="birthday-date"
                    class="datepicker"
                    name="other-driver-birthday"
                    :date="stepModel.ProductSpecific.OtherDriverBirthday"
                    :min-date="minDate"
                    :max-date="maxDate"
                    :year-range="birthdayDateRange"
                    :set-default-date="stepModel.ProductSpecific.OtherDriverBirthdaySelected"
                    @date-changed="dateChanged"
                  />
                  <ValidationError :name="field.name" />
                </Field>
              </div>
            </fieldset>
          </template>
      </div>
        <!-- /*** OTHER DRIVER: DRIVERLICENSE ***/ -->
        <div class="control">
          <label
            for="other-driver-valid-license"
            class="text-question"
            v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipOtherDriverDriverLicense"
          />

          <Field
            name="other-driver-valid-license"
            v-slot="{ field, meta }"
            :rules="{ required: vueModel.CreateClaimEntity.ProductSpecific.OtherDriverValidLicense === null }"
            v-model="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverValidLicense"
          >
            <RadioGroupStacked
              v-bind="field"
              :options="stepModel.ProductSpecific.OtherDriverValidLicenseOptions"
              :model-value="vueModel.CreateClaimEntity.ProductSpecific.OtherDriverValidLicense"
            />
            <ValidationError
              :name="field.name"
              :overrides="{ required: 'Angiv venligst' }"
              :errors="errors"
              v-if="meta.validated && !meta.valid"
            />
          </Field>
        </div>
      </div>
    </Article>

    <!-- /************* WITNESSES QUESTION *************/ -->
    <Article :title="vueModel.ContentViewModel.Step2Car.TitleWitness">
      <div class="control">
        <Field
          name="any-witnesses"
          v-slot="{ field, meta }"
          :rules="{ required: stepModel.ProductSpecific.AnyWitnesses === null }"
          v-model="stepModel.ProductSpecific.AnyWitnesses"
        >
          <RadioGroupStacked
            v-bind="field"
            :options="stepModel.ProductSpecific.AnyWitnessesOptions"
            :model-value="stepModel.ProductSpecific.AnyWitnesses"
            data-test="create-claim-flow-car-any-witnesses"
          />
          <ValidationError
            :name="field.name"
            :errors="errors"
            :overrides="{ required: 'Angiv venligst' }"
            v-if="meta.validated && !meta.valid"
          />
        </Field>
      </div>

      <!-- /************* ADD WITNESESS *************/ -->
      <div v-if="stepModel.ProductSpecific.AnyWitnesses" id="witnesses">
        <div>
          <div v-for="(w, index) in vueModel.CreateClaimEntity.ProductSpecific.WitnessesList" :key="index">
            <label :for="'witness-' + index">
              {{ w.ContactInformation.FirstName }} {{ w.ContactInformation.LastName }}
            </label>
            <span 
              class="editable"
              @click="editWitness(index)"
            />
          </div>
          <div v-if="witnessCount !== 0" class="push-up push-down">
            <a class="plus-minus-link" @click="addWitness" data-test-add-new-witness>
              <span :class="showWitnessForm ? 'minus' : 'plus'" />
              <span v-html="vueModel.ContentViewModel.Step2Car.AddExtraWitnessLinkText" />
            </a>
          </div>
        </div>        
        <witness
          class="push-up"
          v-if="witnessCount === 0 || showWitnessForm"
          :id="'witness-form'"
          :model-value="witness"
          :content="vueModel.ContentViewModel.Step2Car"
          :country-code="vueModel.CreateClaimStep1Model.DefaultCountry.Id"
          data-test-create-claim-flow-car-witness-form
          :errors="errors"
          :editing-witness="editingWitness"
          @save-witness="saveWitness"
          @delete-witness="deleteWitness"
        />
      </div>
    </Article>

    <!-- /************* REPORTED TO POLICE *************/ -->
    <Article id="police-report-filed" :title="vueModel.ContentViewModel.Step2Car.TitlePoliceReport">
      <div class="control">
        <Field
          name="police-report-filed"
          v-slot="{ field, meta }"
          :rules="{ required: stepModel.ProductSpecific.PoliceReportFiled === null }"
          v-model="stepModel.ProductSpecific.PoliceReportFiled"
        >
          <RadioGroupStacked
            v-bind="field"
            :options="stepModel.ProductSpecific.PoliceReportFiledOptions"
            :model-value="stepModel.ProductSpecific.PoliceReportFiled"
          />
          <ValidationError
            :name="field.name"
            :errors="errors"
            :overrides="{ required: 'Angiv venligst' }"
            v-if="meta.validated && !meta.valid"
          />
        </Field>
      </div>

      <div v-if="stepModel.ProductSpecific.PoliceReportFiled">
        <!-- /*** POLICEREPORT: WHEN ***/ -->
        <police-information
          v-model="vueModel.CreateClaimEntity.ProductSpecific.PoliceInformation"
          :content-model="vueModel.ContentViewModel.Step2Car"
          :required="stepModel.ProductSpecific.PoliceReportFiled"
          :claim-event-date="vueModel.CreateClaimStep1Model.ClaimEventDate"
        >
          <template #tooltip1>
            {{ vueModel.ContentViewModel.Step2Car.LabelAndTooltipPoliceReportWhen }}
          </template>

          <template #tooltip2>
            {{ vueModel.ContentViewModel.Step2Car.LabelAndTooltipPoliceReportJournalNumber }}
          </template>

          <template #tooltip3>
            {{ vueModel.ContentViewModel.Step2Car.LabelAndTooltipPoliceReportDistrict }}
          </template>
        </police-information>

        <!-- /*** POLICEREPORT: DRUG TESTED QUESTION ***/ -->
        <div v-if="stepModel.ProductSpecific.AskForDrugTest" class="control">
          <label
            for="driver-substance-tested"
            class="text-question"
            v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipPoliceReportDrugTest"
          />
          <Field
            name="driver-substance-tested"
            v-slot="{ field, meta }"
            :rules="{ required: stepModel.ProductSpecific.DriverSubstanceTested === null }"
            v-model="stepModel.ProductSpecific.DriverSubstanceTested"
          >
            <RadioGroupStacked
              v-bind="field"
              :options="stepModel.ProductSpecific.DriverSubstanceTestedOptions"
              :model-value="stepModel.ProductSpecific.DriverSubstanceTested"
            />

            <ValidationError
              :name="field.name"
              :overrides="{ required: vueModel.ContentViewModel.Step2Car.ErrorMessagePoliceReportDrugTest }"
              :errors="errors"
              v-if="meta.touched && !meta.valid"
            />
          </Field>
        </div>

        <div v-if="stepModel.ProductSpecific.DriverSubstanceTested" id="substance-test-result">
          <!-- /*** POLICEREPORT: DRUG TEST RESULT ***/ -->
          <div class="control">
            <label
              for="substance-test-results"
              class="text-question"
              v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipPoliceReportDrugTestResult"
            />
            <Field
              id="substance-test-results"
              v-model="vueModel.CreateClaimEntity.ProductSpecific.SubstanceTestResult"
              :rules="{ required: true, decimal: [5, ','] }"
              type="text"
              name="substance-test-results"
              :label="vueModel.ContentViewModel.Step2Car.ErrorMessagePoliceReportDrugTestResult"
            />
            <ValidationError name="substance-test-results" />
          </div>

          <!-- /*** POLICEREPORT: DRUG TEST CAUSED REVOKED DRIVERLICENSE ***/ -->
          <div v-if="stepModel.ProductSpecific.AskForLicenseRevoked" class="control">
            <label
              for="license-revoked"
              class="text-question"
              v-html="vueModel.ContentViewModel.Step2Car.LabelAndTooltipPoliceReportDriverLicenseRevoked"
            />
            <Field
              name="license-revoked"
              v-slot="{ field, meta }"
              :rules="{ required: vueModel.CreateClaimEntity.ProductSpecific.LicenseRevoked === null }"
              v-model="vueModel.CreateClaimEntity.ProductSpecific.LicenseRevoked"
            >
              <RadioGroupStacked
                v-bind="field"
                :options="stepModel.ProductSpecific.DriverSubstanceTestedOptions"
                :model-value="vueModel.CreateClaimEntity.ProductSpecific.LicenseRevoked"
              />

              <ValidationError
                :name="field.name"
                :overrides="{ required: vueModel.ContentViewModel.Step2Car.ErrorMessagePoliceReportDriverLicenseRevoked }"
                :errors="errors"
                v-if="meta.touched && !meta.valid"
              />
            </Field>
          </div>
        </div>
      </div>
    </Article>
  </section>
</template>

<script>
import moment from 'moment';
import ReportClaimMixin from '@/components/Feature/Claims/flows/ReportClaimMixin.vue';
import ReportClaimServicesMixin from '@/components/Feature/Claims/flows/ReportClaimServicesMixin.vue';
import Utility from '@/common/UtilityFunctions.js';
import ValidationError from '@/components/UI/ValidationError.vue';
import Witness from '../../components/Witness.vue';
import Upload from '../../components/Upload.vue';
import BaseRadioImages from '@/components/Forms/BaseRadioImages/BaseRadioImages.vue';

export default {
  name: 'ReportClaimCarStep2',
  components: {
    ValidationError,
    Upload,
    Witness,
    BaseRadioImages
  },
  mixins: [ReportClaimMixin, ReportClaimServicesMixin],
  props: {
    vueModel: {
      type: Object,
      default() {
        return {};
      }
    },
    errors: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      minDate: moment(this.vueModel.CreateClaimEntity.ClaimEventDate)
        .subtract(25, 'years')
        .add(1, 'day'),
      maxDate: moment(this.vueModel.CreateClaimEntity.ClaimEventDate).subtract(17, 'years'),
      stepModel: this.vueModel.CreateClaimStep2Model,
      witnessList: this.vueModel.CreateClaimEntity.ProductSpecific.WitnessesList,
      witness: {
        ContactInformation: {
          FirstName: null,
          LastName: null,
          Phone: null,
          Email: null,
          Address: {
            Street: null,
            HouseNumber: null,
            Floor: null,
            Designation: null,
            District: null,
            PostalCode: null,
            City: null
          },
          SelectedAddress: null,
        },
        PersonalRelationToDriver: false,
      },
      showWitnessForm: false,
      witnessIndex: null,
      editingWitness: false
    };
  },
  computed: {
    otherDriverRequired() {
      return (
        this.vueModel.CreateClaimEntity.ProductSpecific.Driver !== null &&
        (this.vueModel.CreateClaimEntity.ProductSpecific.Driver.Type === 3 ||
          this.vueModel.CreateClaimEntity.ProductSpecific.Driver.Type === 2)
      );
    },
    
    otherDriverBirthdayRequired() {
      return (
        this.vueModel.CreateClaimEntity.ProductSpecific.Driver !== null &&
        this.vueModel.CreateClaimEntity.ProductSpecific.Driver.Type === 3
      );
    },
    witnessCount() { 
      return this.vueModel.CreateClaimEntity.ProductSpecific.WitnessesList.length;
    },
    birthdayDateRange() {
      return [this.minDate.year(), this.maxDate.year()];
    },
    showResponsibilityDistribution() {
      return this.vueModel.CreateClaimStep2Model.ProductSpecific.ShowResponsibilityDistribution;
    },
    showRecourseImages() {
      return this.vueModel.CreateClaimStep2Model.ProductSpecific.ShowRecourseImages;
    },
    recourseImageOptions() {
      if (!this.showRecourseImages) {
        return [];
      }

      return this.vueModel.CreateClaimStep2Model.ProductSpecific.RecourseImages.map((obj) => ({
        id: obj.Image.Id,
        name: 'recourse-image-' + obj.Image.Id,
        label: obj.LabelText,
        imgSrc: obj.Image.Src,
        alt: obj.Image.Alt,
        value: obj.RaCode
      }));
    }
  },
  watch: {
    'vueModel.CreateClaimEntity.ProductSpecific.Driver': function (val) {
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverFirstName = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverLastName = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverSelectedAddress = null;
      // this.resetAddressProp(this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverAddress);
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverPhone = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverEmail = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverBirthDate = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverValidLicense = null;
      this.stepModel.ProductSpecific.SelectedDriverAge = null;
      this.stepModel.ProductSpecific.OtherDriverIsBirthdayKnown = null;
      this.resetOtherDriverKnownBirthday();

      return val.ContactId === null ? Utility.nextTickScroll('#other-driver', this) : null;
    },
    'vueModel.CreateClaimEntity.ProductSpecific.OtherDriverSelectedAddress': function () {
      this.updateAddress(
        this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverAddress,
        this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverSelectedAddress
      );
    },
    'stepModel.ProductSpecific.SelectedDriverAge': function (val) {
      if (!this.otherDriverRequired) {
        return;
      }

      if (val == false) {
        this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverBirthDate = null;
        this.stepModel.ProductSpecific.OtherDriverIsBirthdayKnown = null;
      }
    },
    'stepModel.ProductSpecific.OtherDriverIsBirthdayKnown': function (val) {
      if (val == false) {
        this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverBirthDate = moment()
          .subtract(24, 'years')
          .format();
      }
      this.resetOtherDriverKnownBirthday();
    },
    'stepModel.ProductSpecific.PoliceReportFiled': function () {
      this.vueModel.CreateClaimEntity.ProductSpecific.DriverSubstanceTested = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.SubstanceTestResult = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.LicenseRevoked = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.PoliceInformation.PoliceReportFiledDate = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.PoliceInformation.PoliceReportNumber = null;
      this.vueModel.CreateClaimEntity.ProductSpecific.PoliceInformation.PoliceDistrict = null;
      //this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverAgeOptions = null;
    },
    'stepModel.ProductSpecific.DriverSubstanceTested': function (val) {
      return val ? Utility.nextTickScroll('#substance-test-result', this) : null;
    },
    'stepModel.ProductSpecific.AnyWitnesses': function (val) {
      if (val == false)
        this.witnessList.splice(0, this.witnessList.length);
    }
  },
  beforeMount() {
    if (!this.stepModel.ProductSpecific.OtherDriverBirthdaySelected) {
      this.stepModel.ProductSpecific.OtherDriverBirthday = this.maxDate;
    }
  },
  methods: {
    addWitness() {
      this.showWitnessForm = !this.showWitnessForm;
      this.editingWitness = false;
      this.witnessIndex = null;
      this.resetWitness();
      
    },
    saveWitness(witness) {
      this.witnessIndex !== null
        ? this.witnessList[this.witnessIndex] = witness
        : this.witnessList.push(witness);
      
      this.resetWitnessForm();
    },
    editWitness(index) {
      this.witness = this.witnessList[index];
      this.witnessIndex = index;

      this.editingWitness = true;
      this.showWitnessForm = true;
    },
    deleteWitness() {
      this.witnessList.splice(this.witnessIndex, 1);
        // Resets the validation on the witness form if the last witness is deleted
      if (this.witnessList.length === 0)
        this.stepModel.ProductSpecific.AnyWitnesses = false;

      this.$nextTick(() => {
        this.stepModel.ProductSpecific.AnyWitnesses = true;

      });
        this.resetWitnessForm();
      
    },
      resetWitnessForm() {
      this.editingWitness = false;
      this.showWitnessForm = false;
      this.witnessIndex = null;

      this.resetWitness();
    },
      resetWitness() {        
      this.witness = {
        ContactInformation: {
          FirstName: null,
          LastName: null,
          Phone: null,
          Email: null,
          Address: {
            Street: null,
            HouseNumber: null,
            Floor: null,
            Designation: null,
            District: null,
            PostalCode: null,
            City: null,
            CountryRef: null
          },
          SelectedAddress: null,
        }
      };
    },
    getOptions(search, loading) {
      var vm = this;
      loading(true);
      $.get(Utility.constants().externalAPI.dawa_adgangsadresser + search, (resp) => {
        vm.vueModel.options = resp;
        loading(false);
      });
    },
    resetOtherDriverKnownBirthday() {
      this.stepModel.ProductSpecific.OtherDriverBirthday = this.maxDate;
      this.stepModel.ProductSpecific.OtherDriverBirthdaySelected = false;
    },
    dateChanged(param) {
      this.stepModel.ProductSpecific.OtherDriverBirthdaySelected = true;
      this.stepModel.ProductSpecific.OtherDriverBirthday = param;
      this.vueModel.CreateClaimEntity.ProductSpecific.OtherDriverBirthDate = moment(param).format();
    }
  }
};
</script>
